import { useSessionStorage } from 'usehooks-ts';
import { useEffect, useRef } from 'react';
import { CatalogContext } from '@context/catalog.context';
import { useContextSelector } from 'use-context-selector';
import { PLPInnerFeedContext } from '@context/plp-inner-feed.context';

export enum ScrollPositionType {
  ProductTile = 'product',
  PlpInnerFeed = 'plpBreaker',
}

export interface CatalogScrollPosition {
  type: ScrollPositionType;
  productId: string;
  innerFeedId?: string;
}

const useRestoreCatalogScrollPosition = () => {
  const shouldRenderAllProducts = useContextSelector(CatalogContext, (v) => v.shouldRenderAllProducts);
  const setShouldRenderAllProducts = useContextSelector(CatalogContext, (v) => v.setShouldRenderAllProducts);
  const products = useContextSelector(CatalogContext, (v) => v.products);
  const shouldShowLoadingAnimation = useContextSelector(CatalogContext, (v) => v.shouldShowLoadingAnimation);
  const showPlpInnerFeed = useContextSelector(PLPInnerFeedContext, (v) => v.showPlpInnerFeed);
  const [plpScrollPosition, setPlpScrollPosition] = useSessionStorage<CatalogScrollPosition>(
    'plpScrollPositionConfig',
    null,
  );
  const plpScrollPositionRef = useRef<CatalogScrollPosition>(plpScrollPosition);
  const plpLastProductIdRef = useRef<string>(plpScrollPosition?.productId || '');

  const scrollToElementId = (elementId: string) => {
    if (!document.getElementById(elementId)) {
      // if the element is not found, we poll for it

      const intervalDuration = 100; // 100ms
      const maxCount = 5; // max 5 times

      let count = 0;

      const poller = setInterval(() => {
        count++;
        const element = document.getElementById(elementId);
        if (element?.scrollIntoView) {
          // success! found the element so scroll to it
          element.scrollIntoView({ block: 'center' });
          // If we found the element, we clear the interval and the reference.
          clearInterval(poller);
          plpScrollPositionRef.current = null;
        }
        if (count >= maxCount) {
          // if we have tried 5 times and still not found the element, we clear the interval and the reference
          clearInterval(poller);
          plpScrollPositionRef.current = null;
        }
      }, intervalDuration);
      return;
    }

    document.getElementById(elementId)?.scrollIntoView({ block: 'center' });
    plpScrollPositionRef.current = null; // we clear the ref after we scroll
  };

  useEffect(() => {
    if (plpScrollPosition) {
      setShouldRenderAllProducts(true); // if there is a plpScrollPosition, we should render all products
      setPlpScrollPosition(null); // when we land on a new PLP, we always clear the session storage to make sure that we don't carry it forward to other PLPs accidentally
    }

    const noProductsOnPlp = !products || products.length === 0;
    const noScrollPosition = !plpScrollPositionRef.current;

    if (noProductsOnPlp || noScrollPosition || shouldShowLoadingAnimation || !shouldRenderAllProducts) {
      return;
    }

    // this block handles scrolling to PLP inner feed
    if (
      plpScrollPositionRef.current.type === ScrollPositionType.PlpInnerFeed &&
      plpScrollPositionRef.current.innerFeedId
    ) {
      if (!showPlpInnerFeed) {
        return;
      }
      scrollToElementId(`plp_inner_feed_${plpScrollPositionRef.current.innerFeedId}`);
      return;
    }

    // this block handles scrolling to product tile
    if (plpScrollPositionRef.current.type === ScrollPositionType.ProductTile) {
      const matchingProductOnPlp = products.find((x) => String(x.id) === plpScrollPositionRef.current.productId);
      if (!matchingProductOnPlp) {
        plpScrollPositionRef.current = null; // if the product is not on this PLP, we don't scroll to it, and clear ref
        return;
      }
      scrollToElementId(`product_id_${plpScrollPositionRef.current.productId}`);
      return;
    }

    plpScrollPositionRef.current = null; // if we don't have a valid scroll position, we clear the ref
  }, [products, shouldShowLoadingAnimation, shouldRenderAllProducts, showPlpInnerFeed]);

  return { plpLastProductIdRef };
};

export default useRestoreCatalogScrollPosition;
